import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  trackIntegrationConnectFailed,
  trackIntegrationConnectSucceeded,
} from '@/Domain/hooks/Integration/integrationConnectAnalytics';

export const SLACKV2_STATUS_PARAM = 'slackv2_integration_status';
export const SLACKV2_REASON_PARAM = 'reason';

export const SLACKV2_FAILURE_REASONS = [
  'token_exchange',
  'team_already_connected',
  'save',
  'servers_update',
] as const;
export type Slackv2FailureReason = (typeof SLACKV2_FAILURE_REASONS)[number];

const isKnownFailureReason = (value: string | null): value is Slackv2FailureReason =>
  value !== null && (SLACKV2_FAILURE_REASONS as readonly string[]).includes(value);

export const SlackOAuthCallbackListener = (): null => {
  const { t } = useTranslation(['integrations']);
  const { capture } = usePostHogAnalytics();
  const queryClient = useQueryClient();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get(SLACKV2_STATUS_PARAM);
    if (!status) return;

    const reason = params.get(SLACKV2_REASON_PARAM);

    capture(PostHogEventEnum.SlackIntegrationOAuthCallback, {
      status,
      reason,
    });

    if (status === 'connected') {
      trackIntegrationConnectSucceeded(capture, 'slack');
      toast.success(t('slackv2_callback.success.title'), {
        description: t('slackv2_callback.success.description'),
        duration: 6000,
      });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    } else {
      trackIntegrationConnectFailed(capture, 'slack', reason ?? 'unknown');
      const reasonKey = isKnownFailureReason(reason) ? reason : 'default';
      toast.error(t('slackv2_callback.failure.title'), {
        description: t(`slackv2_callback.failure.description.${reasonKey}`),
        duration: 15000,
      });
    }

    params.delete(SLACKV2_STATUS_PARAM);
    params.delete(SLACKV2_REASON_PARAM);
    const query = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }, [capture, queryClient, t]);

  return null;
};
